import type { CityIndexEntry, CityManifest, Venue } from "../domain/types.js";
import { qualityByCategory, websiteHoursEligibleCount } from "../domain/quality.js";

export interface LoadedCity {
  manifest: CityManifest;
  venues: Venue[];
}

async function loadJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not load ${url} (${response.status})`);
  return response.json() as Promise<T>;
}

export function loadCityIndex(): Promise<CityIndexEntry[]> {
  return loadJson<CityIndexEntry[]>("/data/cities.json");
}

export function normalizeVenueCategories(venues: readonly Venue[]): Venue[] {
  return venues.map((venue) => {
    if (
      venue.subtype === "shop=convenience" ||
      venue.subtype === "shop=kiosk" ||
      venue.subtype === "shop=supermarket"
    ) {
      return { ...venue, category: "groceries" };
    }
    if (venue.subtype === "shop=laundry") {
      return { ...venue, category: "services" };
    }
    return venue;
  });
}

export async function loadCity(
  cityId = "brisbane",
  cityIndex?: CityIndexEntry[],
): Promise<LoadedCity> {
  const cities = cityIndex ?? (await loadCityIndex());
  const city = cities.find((entry) => entry.id === cityId);
  if (!city) throw new Error(`City "${cityId}" is not present in the generated city index.`);
  const manifestUrl = new URL(city.manifest, new URL("/data/cities.json", window.location.origin));
  const manifest = await loadJson<CityManifest>(manifestUrl.pathname);
  const venueAssets = Array.isArray(manifest.assets.venues)
    ? manifest.assets.venues
    : [manifest.assets.venues];
  const venueParts = await Promise.all(
    venueAssets.map((asset) => {
      const venueUrl = new URL(asset, manifestUrl);
      return loadJson<Venue[]>(venueUrl.pathname);
    }),
  );
  // Existing schema-v1 assets predate the grocery split. Normalizing on load keeps
  // committed city data current while newly generated assets use the new taxonomy.
  const venues = normalizeVenueCategories(venueParts.flat());
  manifest.quality.byCategory = qualityByCategory(venues);
  manifest.quality.websiteHoursEligibleCount = websiteHoursEligibleCount(venues);
  return { manifest, venues };
}
